import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ServiceLinksBlock from "../components/ServiceLinksBlock";

const HERO_IMG = "/local_img/hero_luis_salom.png";
const SECTION_IMG = "https://ls39motos.com/wp-content/uploads/2025/04/ls-39-motos-luis-salom-01.png";

export default function LuisSalom() {
    const { lang } = useLang();

    const t = lang === "es" ? {
        overline: "Luis Salom · #39",
        title: "El legado de Luis Salom",
        subtitle: "el alma detrás de LS39 Motos",
        intro: "LS39 Motos es mucho más que un concesionario oficial Yamaha en Mallorca. Es un proyecto nacido desde el corazón, guiado por la pasión inquebrantable por las dos ruedas y el recuerdo imborrable de un piloto excepcional que dejó una huella profunda en el motociclismo: Luis Salom. Su emblemático número 39 no solo da nombre a nuestro espacio, sino que define cada uno de nuestros valores.",
        dream_title: "Un sueño hecho realidad por su familia",
        dream_quote: "«Nuestro hijo Luis Salom hubiera montado un concesionario así»",
        dream_p1: "Nadie mejor que María Horrach para realizar esta afirmación tan rotunda y llena de orgullo. Junto a Lluís, su marido y padre del añorado piloto, han cuidado y pulido minuciosamente cada detalle para dar vida a Yamaha LS39 Motos. Este concesionario es el homenaje vivo de una familia que conoce de primera mano el esfuerzo, la dedicación y el amor por las motos.",
        dream_p2: "Cada rincón de nuestra exposición y cada rincón de nuestro taller están impregnados de ese espíritu de superación que Luis demostró en cada circuito del mundo. Para nosotros, mantener vivo su legado significa ofrecer a cada motorista el trato, la calidad y el cuidado que un auténtico campeón se merece.",
        location_p: "Ubicados de forma estratégica en una de las esquinas del final de la Gran Vía Asima, en el Polígono de Son Castelló de Palma, hemos diseñado un espacio moderno, cercano y altamente especializado. Aquí, el asesoramiento personalizado y el servicio técnico oficial se unen para asegurar que tu moto rinda siempre al máximo nivel, bajo la atenta mirada de una historia que nos inspira a mejorar cada día.",
        cta_p: "Te invitamos a visitarnos, a compartir nuestra pasión y a formar parte de este homenaje sobre ruedas. Porque en LS39 Motos, cada kilómetro que recorres lleva un trozo de nuestra historia.",
        cta_btn: "Visítanos en Palma",
    } : {
        overline: "Luis Salom · #39",
        title: "The legacy of Luis Salom",
        subtitle: "the soul behind LS39 Motos",
        intro: "LS39 Motos is much more than an official Yamaha dealership in Mallorca. It is a project born from the heart, guided by an unbreakable passion for two wheels and the unforgettable memory of an exceptional rider who left a deep mark on motorcycling: Luis Salom. His iconic number 39 not only gives our space its name, but defines each and every one of our values.",
        dream_title: "A dream made real by his family",
        dream_quote: "«Our son Luis Salom would have set up a dealership just like this»",
        dream_p1: "No one better than María Horrach to make this bold and proud statement. Together with Lluís, her husband and father of the beloved rider, they have carefully and meticulously crafted every detail to bring Yamaha LS39 Motos to life. This dealership is the living tribute of a family that knows first-hand the effort, dedication and love for motorcycles.",
        dream_p2: "Every corner of our showroom and every corner of our workshop is imbued with the spirit of achievement that Luis demonstrated on every circuit in the world. For us, keeping his legacy alive means offering every rider the treatment, quality and care that a true champion deserves.",
        location_p: "Strategically located at the end of Gran Vía Asima, in the Polígono de Son Castelló in Palma, we have designed a modern, approachable and highly specialised space. Here, personalised advice and official technical service come together to ensure your bike always performs at its peak, under the watchful eye of a history that inspires us to improve every day.",
        cta_p: "We invite you to visit us, share our passion and become part of this tribute on wheels. Because at LS39 Motos, every kilometre you ride carries a piece of our history.",
        cta_btn: "Visit us in Palma",
    };

    return (
        <div data-testid="luis-salom-page">
            {/* HERO */}
            <section className="relative isolate overflow-hidden border-b border-black/10">
                <div className="absolute inset-0 -z-10">
                    <img src={HERO_IMG} alt="Luis Salom #39" className="h-full w-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[--ls-bg]/60 via-[--ls-bg]/40 to-[--ls-bg]" />
                </div>
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
                    <p className="text-overline">{t.overline}</p>
                    <h1 className="mt-6 font-display text-5xl font-bold tracking-tighter sm:text-6xl lg:text-7xl">
                        {t.title}
                        <br />
                        <span style={{ color: "var(--ls-terracotta)" }}>{t.subtitle}</span>
                    </h1>
                </div>
            </section>

            {/* INTRO */}
            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10 lg:py-28">
                <p className="text-base text-neutral-700 leading-relaxed sm:text-lg">{t.intro}</p>
            </section>

            {/* DREAM SECTION */}
            <section className="border-y border-black/10 bg-[--ls-paper]">
                <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-7 space-y-6">
                            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                                {t.dream_title}
                            </h2>
                            <blockquote
                                className="border-l-2 pl-6 font-display text-xl italic text-neutral-600"
                                style={{ borderColor: "var(--ls-terracotta)" }}
                            >
                                {t.dream_quote}
                            </blockquote>
                            <p className="text-base text-neutral-700 leading-relaxed">{t.dream_p1}</p>
                            <p className="text-base text-neutral-700 leading-relaxed">{t.dream_p2}</p>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="relative aspect-[4/5] overflow-hidden border border-black/10">
                                <img
                                    src={SECTION_IMG}
                                    alt="Luis Salom #39"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LOCATION + CTA */}
            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10 space-y-6">
                <p className="text-base text-neutral-700 leading-relaxed">{t.location_p}</p>
                <p className="text-base text-neutral-700 leading-relaxed">{t.cta_p}</p>
                <div className="pt-4">
                    <Link to="/contacto" className="btn-primary">
                        {t.cta_btn} <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            {/* SERVICE LINKS */}
            <ServiceLinksBlock />
        </div>
    );
}
